import { createSelector } from "reselect";

import {
    type AIAgentState,
    createTypedUserMessageDataSelector,
} from "app/pages/sales-intelligence/modules/ai-agent/store";
import { selectFiltersValueDeepEqual } from "app/pages/sales-intelligence/modules/leads-search-filters";
import { hasFiltersDifference } from "app/pages/sales-intelligence/modules/leads-search-filters/utils/filterComparison";
import { selectSalesIntelligenceState } from "app/pages/sales-intelligence/store/selectors";
import { createStatePropertySelector } from "app/pages/workspace/sales/helpers";

import type { LeadGeneratorAgentPayload } from "../types/dto";
import { isApplicationStateWithFilters, isInputFiltersStatePayload } from "../types/dto.guards";

const selectAiAgentState = createStatePropertySelector<AIAgentState>(selectSalesIntelligenceState("aiAgent"));

const selectLeadGeneratorState = selectAiAgentState("leadGenerator");

export const selectLeadGeneratorUserMessageData = createTypedUserMessageDataSelector<LeadGeneratorAgentPayload>();

const selectOutputFiltersValue = createSelector(selectLeadGeneratorState, ({ outputFilters }) => outputFilters);

const selectProcessedMessages = createSelector(selectLeadGeneratorState, ({ processedMessages }) => processedMessages);

export const selectContextualSuggestionsData = createSelector(
    selectLeadGeneratorState,
    ({ contextualSuggestions }) => contextualSuggestions.data,
);

export const selectProcessedMessageData = createSelector(
    [selectProcessedMessages, (_state, messageId?: string) => messageId],
    (processedMessages, messageId) => (messageId ? processedMessages[messageId] : null),
);

export const selectNumberOfGeneratedQueries = createSelector(
    selectLeadGeneratorState,
    ({ numberOfGeneratedQueries }): number => numberOfGeneratedQueries,
);

export const selectLastSentFiltersValue = createSelector(selectLeadGeneratorUserMessageData, (userMessageData) => {
    if (isInputFiltersStatePayload(userMessageData)) {
        return userMessageData.inputFiltersState;
    }

    if (isApplicationStateWithFilters(userMessageData)) {
        return userMessageData.state.filtersState;
    }

    return null;
});

export const selectAreFiltersOutOfSync = createSelector(
    [selectFiltersValueDeepEqual, selectLastSentFiltersValue, selectOutputFiltersValue],
    (currentFilters, lastSentFilters, outputFilters) => {
        const { signalsFilters: currentSignalsFilters, ...currentFiltersWithoutSignals } = currentFilters;

        if (lastSentFilters) {
            return hasFiltersDifference(lastSentFilters, currentFilters);
        }

        if (outputFilters) {
            return hasFiltersDifference(outputFilters, currentFiltersWithoutSignals);
        }

        return false;
    },
);
